import { motion } from "framer-motion";
import { ExternalLink, AlertTriangle, Heart, Waves, Phone, FileText } from "lucide-react";

export default function SASPage() {
  return (
    <div className="pt-28 lg:pt-44 pb-14" data-testid="sas-page">
      {/* Hero */}
      <section className="py-10 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto"
        >
          <h1 className="font-display text-4xl sm:text-5xl text-black mb-4" data-testid="sas-heading">
            Surfers Against Sewage
          </h1>
          <p className="text-charcoal/70 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            Protecting our oceans, beaches and waterways. If you swim, surf or play
            in UK waters, here's how to stay safe and make your voice heard.
          </p>
        </motion.div>
      </section>

      {/* Jax's personal note */}
      <section className="max-w-5xl mx-auto px-6 mb-12" data-testid="sas-personal-note">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-sage/15 to-peachlight rounded-3xl border border-olive/10 p-8 md:p-10"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center flex-shrink-0">
              <Heart size={22} className="text-olive" />
            </div>
            <div>
              <h2 className="font-display text-2xl text-black mb-3">Why I Support Surfers Against Sewage</h2>
              <p className="text-charcoal/70 text-sm md:text-base leading-relaxed mb-3">
                I spend a lot of my life in the sea and it's had a huge impact on my wellbeing. That's
                why I support Surfers Against Sewage and the work they do to campaign for cleaner UK
                waters.
              </p>
              <p className="text-charcoal/70 text-sm md:text-base leading-relaxed">
                If it's something you care about too, you can support them directly.
              </p>
              <a
                href="https://www.sas.org.uk/donate/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 bg-olive text-white hover:bg-eucalyptus rounded-full px-6 py-3 text-sm font-medium transition-all"
                data-testid="sas-personal-donate-btn"
              >
                <Heart size={15} /> Support Surfers Against Sewage
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* About SAS */}
      <section className="max-w-5xl mx-auto px-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-ocean/5 rounded-3xl p-8 md:p-12"
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-ocean/10 flex items-center justify-center flex-shrink-0">
              <Waves size={22} className="text-ocean" />
            </div>
            <div>
              <h2 className="font-display text-2xl text-black mb-2">What is SAS?</h2>
              <p className="text-charcoal/70 text-sm leading-relaxed">
                Surfers Against Sewage is a grassroots environmental charity that campaigns for
                clean oceans, beaches, waves and waterways. They monitor sewage discharges, track
                water quality and fight for better protections for our coastline.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-xl p-5 text-center">
              <p className="font-display text-2xl text-ocean">8,300+</p>
              <p className="text-xs text-charcoal/50 mt-1">Sickness reports since 2019</p>
            </div>
            <div className="bg-white rounded-xl p-5 text-center">
              <p className="font-display text-2xl text-ocean">765</p>
              <p className="text-xs text-charcoal/50 mt-1">Reports in 2026 alone</p>
            </div>
            <div className="bg-white rounded-xl p-5 text-center">
              <p className="font-display text-2xl text-ocean">1,853</p>
              <p className="text-xs text-charcoal/50 mt-1">Reports in 2024</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Report Illness */}
      <section className="max-w-5xl mx-auto px-6 mb-12" data-testid="sas-report-section">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-sunset/10 rounded-3xl p-8 md:p-12 border border-sunset/20"
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-sunset/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={22} className="text-sunset" />
            </div>
            <div>
              <h2 className="font-display text-2xl text-black mb-2">Feeling Ill After Swimming?</h2>
              <p className="text-charcoal/70 text-sm leading-relaxed">
                If you've been unwell after swimming, surfing, or paddling in UK waters, reporting
                your symptoms helps build evidence for cleaner waters. Common symptoms include
                gastroenteritis, ear/nose/throat infections, skin rashes and respiratory issues.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-5">
              <h3 className="font-medium text-sm text-black mb-2">How to report</h3>
              <ol className="text-sm text-charcoal/70 space-y-2 list-decimal list-inside">
                <li>Visit the SAS Sickness Report Form (link below)</li>
                <li>Provide details about where and when you were in the water</li>
                <li>Describe your symptoms and when they started</li>
                <li>Note if you've seen a doctor — 79% of doctors attribute these illnesses to sewage</li>
              </ol>
            </div>
            <a
              href="https://www.sas.org.uk/water-quality/submit-a-sickness-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-sunset text-white hover:bg-sunset/80 rounded-full px-8 py-3.5 text-sm font-medium transition-all w-full sm:w-auto sm:inline-flex"
              data-testid="sas-report-illness-btn"
            >
              <FileText size={16} />
              Submit a Sickness Report
              <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>
      </section>

      {/* Water Quality Alerts */}
      <section className="max-w-5xl mx-auto px-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-sage/10 rounded-3xl p-8 md:p-12"
        >
          <h2 className="font-display text-2xl text-black mb-4">Check Water Quality Before You Swim</h2>
          <p className="text-charcoal/70 text-sm leading-relaxed mb-6">
            Before heading to the beach or river, check real-time sewage discharge alerts
            and water quality forecasts using these free tools:
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href="https://www.sas.org.uk/water-quality/sewage-pollution-alerts/safer-seas-rivers-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-5 hover:shadow-md transition-shadow group"
              data-testid="sas-safer-seas-link"
            >
              <h3 className="font-medium text-sm text-black mb-1 group-hover:text-sage transition-colors">
                Safer Seas & Rivers Service
                <ExternalLink size={12} className="inline ml-1" />
              </h3>
              <p className="text-xs text-charcoal/50">
                Real-time sewage discharge alerts and local water quality forecasts via the SAS app or website.
              </p>
            </a>
            <a
              href="https://environment.data.gov.uk/bwq/profiles/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-5 hover:shadow-md transition-shadow group"
              data-testid="sas-swimfo-link"
            >
              <h3 className="font-medium text-sm text-black mb-1 group-hover:text-sage transition-colors">
                Swimfo (Environment Agency)
                <ExternalLink size={12} className="inline ml-1" />
              </h3>
              <p className="text-xs text-charcoal/50">
                Official designated bathing water quality profiles and classifications for England.
              </p>
            </a>
          </div>
        </motion.div>
      </section>

      {/* Get Involved */}
      <section className="max-w-5xl mx-auto px-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-2xl text-black mb-6 text-center">Get Involved</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <a
              href="https://www.sas.org.uk/donate/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 border border-black/5 hover:shadow-md transition-shadow text-center group"
              data-testid="sas-donate-link"
            >
              <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-3">
                <Heart size={18} className="text-sage" />
              </div>
              <h3 className="font-medium text-sm text-black mb-1">Donate to SAS</h3>
              <p className="text-xs text-charcoal/50">Support their campaigns for cleaner water</p>
            </a>
            <a
              href="https://www.sas.org.uk/news/million-mile-clean/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 border border-black/5 hover:shadow-md transition-shadow text-center group"
              data-testid="sas-beach-clean-link"
            >
              <div className="w-10 h-10 rounded-full bg-ocean/10 flex items-center justify-center mx-auto mb-3">
                <Waves size={18} className="text-ocean" />
              </div>
              <h3 className="font-medium text-sm text-black mb-1">Join a Beach Clean</h3>
              <p className="text-xs text-charcoal/50">Million Mile Clean events near you</p>
            </a>
            <a
              href="https://www.sas.org.uk/water-quality/sewage-pollution-alerts/safer-seas-rivers-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 border border-black/5 hover:shadow-md transition-shadow text-center group"
              data-testid="sas-app-link"
            >
              <div className="w-10 h-10 rounded-full bg-amber/10 flex items-center justify-center mx-auto mb-3">
                <Phone size={18} className="text-amber" />
              </div>
              <h3 className="font-medium text-sm text-black mb-1">Get the SAS App</h3>
              <p className="text-xs text-charcoal/50">Real-time alerts before you enter the water</p>
            </a>
          </div>
        </motion.div>
      </section>

      {/* Contact SAS */}
      <section className="max-w-5xl mx-auto px-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="font-display text-2xl text-black mb-3">Contact Surfers Against Sewage</h2>
          <p className="text-charcoal/60 text-sm mb-6">
            Have questions or want to get in touch with SAS directly?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.sas.org.uk/contact-us/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-olive text-white hover:bg-olive/80 rounded-full px-6 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
              data-testid="sas-contact-btn"
            >
              Contact SAS <ExternalLink size={14} />
            </a>
            <a
              href="https://www.sas.org.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-charcoal/20 text-charcoal hover:bg-white/50 rounded-full px-6 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
              data-testid="sas-website-btn"
            >
              Visit sas.org.uk <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
